import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Home,
  LoaderCircle,
  LogIn,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { verifiedProfilesCache } from '../lib/verifiedProfilesCache';
import { supabase } from '../lib/supabase';
import { DEFAULT_STUDY_ROUTE } from '../lib/studyNavigation';
import {
  clearPrePaymentMarkers,
  isMatchingPrePaymentUser,
  readPrePaymentMarkers,
} from '../utils/authFlow';
import useSEO from '../hooks/useSEO';
import MarketingLayout from '../components/MarketingLayout';
import '../components/public-folio.css';
import {
  logApBioOnboardingEvent,
  readStoredApBioOnboardingContext,
} from '../lib/apBioOnboarding';

const PHASE = {
  LOADING_AUTH: 'loading-auth',
  RESTORING_SESSION: 'restoring-session',
  REFRESHING_PROFILE: 'refreshing-profile',
  REDIRECTING: 'redirecting',
  SIGNIN_FALLBACK: 'sign-in-fallback',
};

const getPostPaymentRoute = () => {
  try {
    const stored = sessionStorage.getItem('pre-payment-location');
    if (stored && stored.startsWith('/')) return stored;
  } catch (error) {
    // Non-blocking.
  }
  return DEFAULT_STUDY_ROUTE;
};

const buildSigninRedirect = (redirectPath, sessionId) => {
  const safeRedirect = redirectPath || DEFAULT_STUDY_ROUTE;
  const base = `/signin?payment_success=true&redirect=${encodeURIComponent(safeRedirect)}`;
  if (!sessionId) return base;
  return `${base}&session_id=${encodeURIComponent(sessionId)}`;
};

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const { user, refreshProfileCache, loading } = useAuth();
  const [phase, setPhase] = useState(PHASE.LOADING_AUTH);
  const [detail, setDetail] = useState('Checking your APDoneRight account...');
  const recoveryAttemptedRef = useRef(false);
  const redirectedRef = useRef(false);
  const mountedRef = useRef(true);

  useSEO({
    title: 'Payment success | APDoneRight',
    description: 'Finishing your APDoneRight checkout and restoring your account access.',
    canonical: '/payment-success',
    robots: 'noindex,follow',
  });

  const sessionId = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('session_id');
  }, []);
  const postPaymentRoute = useMemo(() => getPostPaymentRoute(), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    localStorage.setItem('paymentCompleted', 'true');
    sessionStorage.setItem('payment-success', 'true');
  }, [sessionId]);

  const navigateToSignin = useCallback((sessionIdFromFlow = sessionId) => {
    clearPrePaymentMarkers();
    setPhase(PHASE.SIGNIN_FALLBACK);
    setDetail('We could not safely reconnect the payment to your account. Please sign in again.');
    navigate(buildSigninRedirect(postPaymentRoute, sessionIdFromFlow), { replace: true });
  }, [navigate, postPaymentRoute, sessionId]);

  const startHomepageRedirect = useCallback((authenticatedUser) => {
    if (!authenticatedUser?.id || redirectedRef.current) return;

    redirectedRef.current = true;
    setPhase(PHASE.REDIRECTING);
    setDetail('Payment confirmed. Taking you into the tutor now...');

    verifiedProfilesCache.delete(authenticatedUser.id);

    try {
      sessionStorage.removeItem(`verifiedProfile-${authenticatedUser.id}`);
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i);
        if (key && key.includes(authenticatedUser.id) && key.includes('profile')) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('[PaymentSuccess] Could not clear cached profile data', error);
    }

    clearPrePaymentMarkers();
    setPhase(PHASE.REFRESHING_PROFILE);
    setDetail('Your plan is active. Refreshing your account in the background...');

    void refreshProfileCache(authenticatedUser.id).catch((error) => {
      console.warn('[PaymentSuccess] Background profile refresh failed', error);
    });

    const onboardingContext = readStoredApBioOnboardingContext(authenticatedUser.id);
    if (onboardingContext) {
      void logApBioOnboardingEvent({
        userId: authenticatedUser.id,
        eventName: 'trial_started',
        step: 3,
        metadata: {
          onboardingContext,
          destination: postPaymentRoute,
        },
      });
    }

    try {
      sessionStorage.removeItem('pre-payment-location');
    } catch (error) {
      // Non-blocking.
    }

    navigate(postPaymentRoute, { replace: true });
  }, [navigate, postPaymentRoute, refreshProfileCache]);

  const restoreMatchingSession = useCallback(async (prePaymentMarkers) => {
    if (recoveryAttemptedRef.current) return;
    recoveryAttemptedRef.current = true;

    if (!prePaymentMarkers?.userId) {
      navigateToSignin();
      return;
    }

    setPhase(PHASE.RESTORING_SESSION);
    setDetail('Restoring the account that started this checkout...');

    const resolveMatch = (candidateUser) => {
      if (!candidateUser?.id) return null;
      return isMatchingPrePaymentUser(candidateUser.id, prePaymentMarkers) ? candidateUser : null;
    };

    try {
      let matchedUser = null;

      const { data: existingData, error: existingError } = await supabase.auth.getSession();
      if (!existingError) {
        matchedUser = resolveMatch(existingData?.session?.user);
      }

      const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession();
      if (!refreshError) {
        const refreshedUser = resolveMatch(refreshedData?.session?.user);
        if (refreshedUser) return refreshedUser;
      }

      const { data: finalData, error: finalError } = await supabase.auth.getSession();
      if (!finalError) {
        const finalUser = resolveMatch(finalData?.session?.user);
        if (finalUser) return finalUser;
      }

      if (matchedUser) return matchedUser;
    } catch (error) {
      console.error('[PaymentSuccess] Session restoration failed', error);
    }

    return null;
  }, [navigateToSignin]);

  useEffect(() => {
    if (redirectedRef.current) return;

    const prePaymentMarkers = readPrePaymentMarkers();

    if (loading) {
      setPhase(PHASE.LOADING_AUTH);
      setDetail('Checking your APDoneRight account...');
      return;
    }

    if (user) {
      if (!prePaymentMarkers?.userId || !isMatchingPrePaymentUser(user.id, prePaymentMarkers)) {
        void restoreMatchingSession(prePaymentMarkers).then((matchedUser) => {
          if (!mountedRef.current || redirectedRef.current) return;
          if (matchedUser) {
            startHomepageRedirect(matchedUser);
            return;
          }
          navigateToSignin();
        });
        return;
      }

      startHomepageRedirect(user);
      return;
    }

    void restoreMatchingSession(prePaymentMarkers).then((matchedUser) => {
      if (!mountedRef.current || redirectedRef.current) return;
      if (matchedUser) {
        startHomepageRedirect(matchedUser);
        return;
      }
      navigateToSignin();
    });
  }, [loading, restoreMatchingSession, startHomepageRedirect, navigateToSignin, user]);

  const phaseCopy = {
    [PHASE.LOADING_AUTH]: {
      eyebrow: 'Securing your upgrade',
      title: 'Confirming your payment',
      description: detail,
      tone: 'loading',
    },
    [PHASE.RESTORING_SESSION]: {
      eyebrow: 'Reconnecting your account',
      title: 'Restoring your session',
      description: detail,
      tone: 'loading',
    },
    [PHASE.REFRESHING_PROFILE]: {
      eyebrow: 'Upgrade locked in',
      title: 'Subscription activated',
      description: detail,
      tone: 'success',
    },
    [PHASE.REDIRECTING]: {
      eyebrow: 'Upgrade locked in',
      title: 'Heading to the tutor',
      description: detail,
      tone: 'success',
    },
    [PHASE.SIGNIN_FALLBACK]: {
      eyebrow: 'One more step',
      title: 'Please sign in again',
      description: detail,
      tone: 'fallback',
    },
  }[phase];

  const isBusy = phase === PHASE.LOADING_AUTH || phase === PHASE.RESTORING_SESSION;

  return (
    <MarketingLayout
      showNavbar={false}
      showFooter={false}
      className="ap-payment-folio"
      mainClassName="ap-payment-folio-main flex flex-1 items-center justify-center px-4 py-10 sm:px-6"
    >
      <div className="relative w-full max-w-3xl overflow-hidden rounded-[32px] border border-teal-100/80 bg-white/88 shadow-[0_28px_90px_rgba(16,24,40,0.10)] backdrop-blur-xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-400 via-teal-500 to-cyan-500" />

        <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="relative overflow-hidden px-6 py-8 sm:px-8 sm:py-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.12),_transparent_36%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.10),_transparent_34%)]" />

            <div className="relative">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] text-slate-500 uppercase transition-colors hover:text-slate-700"
              >
                <ShieldCheck className="h-4 w-4 text-teal-500" />
                APDoneRight
              </Link>

              <div className="mt-10 max-w-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-teal-600">
                  {phaseCopy.eyebrow}
                </p>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                  {phaseCopy.title}
                </h1>
                <p className="mt-4 max-w-lg text-base leading-7 text-slate-600 sm:text-lg">
                  {phaseCopy.description}
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate(postPaymentRoute, { replace: true })}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
                >
                  <Home className="h-4 w-4" />
                  Go to tutor
                </button>

                <button
                  type="button"
                  onClick={() => navigate(buildSigninRedirect(postPaymentRoute, sessionId), { replace: true })}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-transform duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:text-slate-950"
                >
                  <LogIn className="h-4 w-4" />
                  Sign in again
                </button>
              </div>
            </div>
          </section>

          <aside className="border-t border-teal-100/70 bg-slate-950 px-6 py-8 text-white lg:border-l lg:border-t-0 sm:px-8 sm:py-10">
            <div className="flex h-full flex-col justify-between gap-8">
              <div>
                <div className="flex items-center justify-between rounded-[24px] border border-white/10 bg-white/5 px-5 py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-teal-200/80">
                      Checkout status
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {phase === PHASE.SIGNIN_FALLBACK ? 'Needs re-authentication' : 'Payment received'}
                    </p>
                  </div>

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                    {isBusy ? (
                      <LoaderCircle className="h-7 w-7 animate-spin text-teal-300" />
                    ) : (
                      <CheckCircle2 className="h-7 w-7 text-teal-300" />
                    )}
                  </div>
                </div>

                <div className="mt-6 rounded-[24px] border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-teal-300" />
                    <p className="text-sm font-medium text-teal-100">
                      {phase === PHASE.SIGNIN_FALLBACK
                        ? 'We are protecting the account that started the checkout.'
                        : 'Your payment flags stay in place until the paid profile is verified.'}
                    </p>
                  </div>

                  <div className="mt-5 space-y-3 text-sm text-slate-300">
                    <div className="flex items-start gap-3">
                      <ArrowRight className="mt-0.5 h-4 w-4 text-teal-300" />
                      <p>We verify that the returning session belongs to the same user who started checkout.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <ArrowRight className="mt-0.5 h-4 w-4 text-teal-300" />
                      <p>After confirmation, account refresh happens in the background so you are not held on this page.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <ArrowRight className="mt-0.5 h-4 w-4 text-teal-300" />
                      <p>If anything looks off, signing in again will safely reconnect you to the upgraded account.</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs leading-6 text-slate-400">
                Built for a fast handoff back into APDoneRight without leaving the billing state ambiguous.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default PaymentSuccessPage;
